/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'text!./templates/filterDock.html',
	'jquery',
	'./FilterDockItemController',
	'../utils/FilterMenu',
	'./FilterItemsContainer',
	'./ActiveFilterIndicator',
	'../../features/dashboard/dnD/api/impl/MetadataDnD',
	'../../dataSources/utils/DatasourceUtil',
	'underscore'
], (BaseView, FilterDockTemplate, $, FilterDockItemController, FilterMenu, FilterItemsContainer, ActiveFilterIndicator, MetadataDnD, DatasourceUtil, _) => {

	const TRASITION_ENDED = 'webkitTransitionEnd otransitionend oTransitionEnd msTransitionEnd transitionend';

	const FilterDockView = BaseView.extend({
		templateString: FilterDockTemplate,
		className: 'filterDock',

		events: {
			'primaryaction .filter-dock-begin': 'clickFilterDockIcon',
			'primaryaction .filterDockChevronLeft': 'clickOnLeftChevron',
			'primaryaction .filterDockChevronRight': 'clickOnRightChevron',
			'primaryaction .filter-dock-overflow': 'toggleDropdownMenu',
			'touchend .filter-dock-overflow': 'toggleDropdownMenu',
			'escapeaction .filter-dock-overflow, .filter-menu': 'closeDropdownMenu'
		},

		scope: null,
		label: null,
		tooltip: null,
		dropZoneTooltip: null,

		chevronAnimationDuration: 300,

		/**
		 * Constructor
		 *
		 * @param {object} args - arguments
		 * @param {object} additionalParams - additional parameters
		 * @param {string} additionalParams.scope - scope the view is in
		 * @param {string} additionalParams.className - additional class names for the view
		 * @param {string} additionalParams.id - id of the view
		 * @param {string} additionalParams.label - label on the view
		 * @param {string} additionalParams.tooltip - tooltip on the view
		 * @param {string} additionalParams.dropZoneTooltip - tooltip for filter drop zone on the view
		 *
		 * @constructs FilterDockView
		 * @extends {lib/@waca/core-client/js/core-client/ui/core/View}
		 */
		init(args, additionalParams) {
			this.handlers = {};

			if (additionalParams.className) {
				this.className += ' ' + additionalParams.className;
			}
			this.id = additionalParams.id;
			this.scope = additionalParams.scope;
			this.selector = {
				origin: 'filter',
				scope: this.scope
			};
			this.datasource = '';
			this.label = additionalParams.label;
			this.tooltip = additionalParams.tooltip;
			this.dropZoneTooltip = additionalParams.dropZoneTooltip;
			this.overflowMenuAriaLabel = additionalParams.overflowMenuAriaLabel;
			this.chevron_left = additionalParams.chevron_left;
			this.chevron_right = additionalParams.chevron_right;
			this.parentLayoutId = additionalParams.parentLayoutId;
			this.singleDockMode = additionalParams.singleDockMode;
			this.excludeAddToCanvas = additionalParams.excludeAddToCanvas;
			FilterDockView.inherited('init', this, arguments);

			this.onClickFilterDockIcon = args.handlers.onClickFilterDockIcon;
			this.onAddRemoveFilterItem = args.handlers.onAddRemoveFilterItem;

			this.ajaxSvc = additionalParams.ajaxSvc;

			this.dashboardApi = args.dashboardApi;
			this._iconsFeature = this.dashboardApi.getFeature('Icons');
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');

			this.pageContextAPI = args.pageContextAPI;

			this.viewModel = args.viewModel;
			this.authorHint = args.authorHint;
			this.consumeHint = args.consumeHint;

			this.filterItemsMap = {};

			// Create drop down action menu and after this view renders render the menu
			this.menu = new FilterMenu({
				menuActionHandler: args.handlers.menuActionHandler
			});

			//listen for browser window resize, adjust scrollbars as needed
			this.handlers.windowResizeHandler = this._evaluateScrollState.bind(this);
			this.handlers.animationFinished = this.trigger.bind(this, 'animation-finished');
			$(window).on('resize', this.handlers.windowResizeHandler);
			this.on('resize', this._evaluateScrollState, this);
			this.$el.on(TRASITION_ENDED, this.handlers.animationFinished);

			this.afi = new ActiveFilterIndicator({
				scopes: this.scope === 'global' ? {
					globalScope: 'global'
				} : {
					tabScope: this.scope
				},
				pageContextAPI: this.pageContextAPI,
				location: 'Icon'
			});
			this.populateFilterItemsPayload = [];
		},

		_evaluateScrollState() {
			this._setChevronActiveStates();
			this._enableScrolling(this._shouldWeEnableScrolling());
		},

		/**
		 * Render view
		 *
		 * @return {Promise} promise to be resolved when rendering is done
		 */
		render(isAuthoringMode) {
			this.$el.html(this.dotTemplate({
				scope: this.scope,
				label: this.label,
				tooltip: this.tooltip,
				dropZoneTooltip: this.dropZoneTooltip,
				chevron_left: this.chevron_left,
				chevron_right: this.chevron_right,
				overflowMenuAriaLabel: this.overflowMenuAriaLabel,
				emptyLabel: isAuthoringMode ? this.authorHint : this.consumeHint,
				isAuthoring: isAuthoringMode,
				filterDockIcon: this._iconsFeature.getIcon('filter').id
			}));
			this._getItemsEl();

			return this.menu.render({
				viewModel: this.viewModel,
				mountNode: this.getFilterMenuMountNode()
			}).then(() => {
				if (!isAuthoringMode) {
					this.hideMenu();
				}
				this.populateFilterItems();
				this.$el.find('.filterDockIconContainer').append(this.afi.getNode());
				this.afi.setCount(this.afi.getActiveCount());
				this.initalizeDnD();
			});
		},

		/**
		 * Populates filter items. This function is executed sequencialy when invoked multiple times.
		 *
		 * @param {object} payload - the payload use to populate the filter items
		 */
		populateFilterItems(payload) {

			payload = payload || {};

			if (payload.isInScope && !payload.isInScope(this.scope)) {
				return Promise.resolve();
			}

			if (!_.isEmpty(payload)) {
				this.populateFilterItemsPayload.push(payload);
			}

			let _payloadIsAdd = (payload) => {
				return $.isFunction(payload.isAdd) ? payload.isAdd() : false;
			};

			let _populateFilterItems = (payload) => {
				if (!this.payloadInProgress) {
					this.payloadInProgress = payload;
					let updateAll = $.isFunction(payload.isGeneralUpdate) ? payload.isGeneralUpdate() : false;

					if (updateAll || payload.beforeItem) {
						//store where we currently are scrolled to.
						this._currentScrollLeft = this._getItemsEl().scrollLeft();
						this.removeAllFilterItems();
					}
					return this.renderItems()
						.then(this._setupFilterItemWrapperInteraction.bind(this))
						.finally(() => {
							this.payloadInProgress = null;
							let currPayload = this.populateFilterItemsPayload.pop();
							if (this.populateFilterItemsPayload.length > 0) {
								return _populateFilterItems(currPayload);
							} else if (currPayload && this.populateFilterItemsPayload.length === 0) {

								this._setChevronActiveStates();
								this._enableScrolling(this._shouldWeEnableScrolling());

								this.populateFilterItemsPayload = [];

								// focus on the filter dock only if payload is added
								if (_payloadIsAdd(currPayload)) {
									this.$el.find('.filter-dock-middle').focus();
								}
							}
						});
				} else {
					return Promise.resolve();
				}
			};

			return _populateFilterItems(payload);

		},
		/**
		 * @returns the JQuery element for the filter-items-container
		 */
		_getItemsEl() {
			if (!this.$filterItemsContainer) {
				this.$filterItemsContainer = this.$el.find('.filter-items-container');
			}
			return this.$filterItemsContainer;
		},

		/**
		 * @return the JQuery element to insert before
		 */
		_getElementToInsertBefore() {
			if (!this.$insertBefore) {
				const itemsEl = this._getItemsEl();
				this.$insertBefore = itemsEl.find('.filterInsertMarker.atBack');
			}

			return this.$insertBefore;
		},

		/**
		 * @return should we enable scrolling for this dock view.
		 */
		_shouldWeEnableScrolling() {
			// Get the filter items element
			const $itemsEl = this._getItemsEl();
			const visibleWidth = $itemsEl[0].offsetWidth;
			const scrollWidth = $itemsEl[0].scrollWidth;

			return scrollWidth > visibleWidth;
		},
		/**
		 * The following will render/update all filter items in this dock view.
		 */
		renderItems() {

			// Get the items from the page context and render each of them.
			const items = this.pageContextAPI.getPageContextItems(this.selector);

			let nextReady = Promise.resolve();

			//in case we have to open a dropdown for an added item
			const _openDropDownForItems = [];
			items.forEach((item) => {
				if (item.shouldOpenViewOnLoad()) {
					_openDropDownForItems.push(item);
				}

				nextReady = nextReady.then(() => {
					return this.addFilterItem(item, null);
				});
			});

			return nextReady.then(() => {

				//reset our scroll position if possible.
				if (this._currentScrollLeft && this._currentScrollLeft > 0) {
					this._getItemsEl().scrollLeft(this._currentScrollLeft);
					this._currentScrollLeft = 0;
				}

				//open up dropdowns for items that require it
				_openDropDownForItems.forEach((item) => {
					this._showDropdownFilterForItem(item);
				});

				const empty = (!items || items.length === 0);
				this._toggleEmptyView(empty);
				this.menu.toggleDisableOverflow(empty);

				// Now that the items are rendered, determine scroll and chevron states.
				this._enableScrolling(this._shouldWeEnableScrolling());
				this._setChevronActiveStates();
			});

		},

		changeToConsumeMode() {
			this.$el.find('.empty-view > span').text(this.consumeHint);
			this.hideMenu();
		},

		changeToAuthorMode() {
			this.$el.find('.empty-view > span').text(this.authorHint);
			this.showMenu();
		},

		/**
		 * Remove all filter items from this dock view.
		 */
		removeAllFilterItems() {
			for (const dockKey in this.filterItemsMap) {
				if (this.filterItemsMap.hasOwnProperty(dockKey)) {
					this.removeFilterItem(this.filterItemsMap[dockKey]);
				}
			}
		},

		switchTabScope(newScope) {
			this.scope = newScope;
			this.selector.scope = this.scope;
			this.afi.setScope('tabScope', this.scope);
			this.afi.setCount(this.afi.getActiveCount());
			this.removeAllFilterItems();
			this.renderItems();
			this._setupFilterItemWrapperInteraction();
		},

		addDatasourceToFilterSelector(newDatasource) {
			this.datasource = newDatasource;
			this.selector.sourceId = this.datasource;
			this.selector._strictMatch = true;
			if (!this.datasource) {
				delete this.selector.sourceId;
				delete this.selector._strictMatch;
			}
		},

		/* when scope is the same but datasource is different */
		switchDatasource(newDatasource, extraOptions) {
			this.addDatasourceToFilterSelector(newDatasource);
			if (extraOptions && extraOptions.silent)  {
				return;
			}
			this.afi.setScope('tabScope', this.scope);
			this.afi.setCount(this.afi.getActiveCount());
			this.removeAllFilterItems();
			this.renderItems();
			this._setupFilterItemWrapperInteraction();
		},

		initalizeDnD() {
			const accepts = function (dragObject) {
				let metadataColumns = _.map(dragObject && dragObject.data && dragObject.data.columns, (column) => column.metadataColumn);
				if (metadataColumns.length === 0) {
					return false;
				}

				// Don't allow OLAP measures since we can't create pre-aggregate filters with them
				for (let i = 0; i < metadataColumns.length; i++) {
					if (!DatasourceUtil.metadataColumnSupportsFiltering(metadataColumns[i])) {
						return false;
					}
				}
				return MetadataDnD.canAccepts(dragObject, {
					fromFilterDock: true
				});
			};

			const DndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			this._dndTarget = DndManager.addDropTarget(this.$el, {
				accepts: accepts.bind(this),
				onDragMove: this.onDragOver.bind(this),
				onDragLeave: this.onDragLeave.bind(this),
				onDrop: this.onDrop.bind(this),
				priority: -100
			});
		},

		/**
		 * Called by the drag&drop manager when a drop happens
		 */
		onDrop(dragObject, dropTargetNode, event) {
			if (!this.itemsContainer) {
				return;
			}

			this.itemsContainer.clearIntervals();
			this.itemsContainer.cleanupInsertMarker();
			const position = this.itemsContainer.getDropPosition(event);

			if (position && position.isValid) {
				this._updateFilterContext (dragObject, position);
			}
		},

		_updateFilterContext (dragObject, position) {
			const undoRedoTransactionId = _.uniqueId('_filterDockOnDrop_');
			let prevItem = null;

			let metadataColumns = _.map(dragObject && dragObject.data && dragObject.data.columns, (column) => column.metadataColumn);
			// Process in reverse order so that correct beforeItem is used.
			for (let idx = metadataColumns.length - 1; idx >= 0; idx--) {
				let metadataColumn = metadataColumns[idx];
				const contextItem = {
					sourceId: dragObject.data.sourceId,
					itemId: metadataColumn.getId(),
					itemName: metadataColumn.getLabel(),
					dataType: metadataColumn.getDataType(),
					usage: metadataColumn.getType(),
					scope: this.scope,
					isHierarchy: metadataColumn.isHierarchy(),
					isNamedSet: metadataColumn.isNamedSet(),
					openViewOnLoad: metadataColumns.length === 1 && !metadataColumn.isNamedSet(),
					isModeledFilter: metadataColumn.getObjectType() === 'Filter'
				};

				const command = {
					command: 'update'
				};

				if (this._getPageContextType(metadataColumn.getDataType(), metadataColumn.getType()) === 'range') {
					command.condition = {};
				} else {
					command.values = [];
					let members = dragObject.data.utils.getColumnsWithMembers()[metadataColumn.getId()];
					_.each(members, filter => command.values.push({
						u: filter.id,
						d:filter.memberCaption
					}));
					contextItem.openViewOnLoad = contextItem.openViewOnLoad && command.values.length === 0;
				}

				const beforeItem = prevItem || (position.insertBefore && position.insertBefore.item);
				this.pageContextAPI.updateFilterContext(
					contextItem,
					command, {
						beforeItem: beforeItem,
						initialDrop: true,
						undoRedoTransactionId: undoRedoTransactionId
					}
				);
				prevItem = this.pageContextAPI.getPageContextItem(contextItem);
			}
		},

		_getPageContextType(dataType, usage) {
			if (usage === 'fact') {
				return 'range';
			}

			// usage is attribute: non numeric, tree, or date time filter
			const isDateType = dataType === 'date' || dataType === 'time' || dataType === 'datetime' || dataType === 'timestamp';
			return isDateType ? 'range' : 'tupleset';
		},

		/**
		 * Handles the skip over 'dragover' event case
		 * @param {object} event - the JQuery event object
		 * @param {object} [options] - the options contains context to process the dragover event
		 * @param {string} [options.dropZoneSelector] - The drop zone selector
		 * @param {string} [options.prevZone] - The previous zone
		 * @param {object} [options.dataTransferData] - The The dataTransfer's drag data
		 */
		handleSkipDragOver(event, options) {
			this.onDragOver(event, options);
		},

		/**
		 * Called by the drag&drop manager when move inside a drop zone
		 */
		onDragOver(dragObject, dropNode, event) {
			if (this.itemsContainer) {
				this.itemsContainer.onDragHoverFilterDock(dragObject, dropNode, event);
			}
		},

		/**
		 * Called by the drag&drop manager when the drag leaves
		 */
		onDragLeave() {
			if (this.itemsContainer) {
				this.itemsContainer.clearIntervals();
			}
		},

		/**
		 * Add a new item to this filter dock or update the existing one.
		 * @param item - the item to add/update
		 * @param {object} position - (optional) contains position info as to where the item should be rendered on the filter dock
		 *			      If not specify then add to the end of the filter dock
		 */
		addFilterItem(item, position) {

			//a promise that resolves with true if a new item was added.
			const addOrUpdateDone = new Promise((resolve) => {
				const filterItemKey = FilterDockItemController.genFilterItemDockKey(item);
				const existingFilterItem = this.filterItemsMap[filterItemKey];
				if (existingFilterItem) {
					existingFilterItem.update();
					resolve(null);
				} else {
					const ctlOpts = {
						item: item,
						pageContextAPI: this.pageContextAPI,
						dashboardApi: this.dashboardApi,
						scope: this.scope,
						ajaxSvc: this.ajaxSvc,
						dockKey: filterItemKey,
						onDelete: this.removeFilterItem.bind(this),
						getFilterItem: this.getFilterItem.bind(this),
						parentLayoutId: this.parentLayoutId,
						singleDockMode: this.singleDockMode,
						excludeAddToCanvas: this.excludeAddToCanvas
					};

					const filterDockItemCtl = new FilterDockItemController(ctlOpts);
					this.filterItemsMap[filterItemKey] = filterDockItemCtl;
					this.onAddRemoveFilterItem(item, this.scope, true);

					filterDockItemCtl.ready.then(() => {
						const $itemsEl = this._getItemsEl();
						const items = $itemsEl.find('.filterItemWrapper').get();

						if (!filterDockItemCtl.view) {
							resolve();
						} else if (items.length === 0 || !position || !position.insertBefore || !position.insertBefore.node) {
							const $beforeNode = this._getElementToInsertBefore();
							if ($beforeNode && $beforeNode.length > 0) {
								filterDockItemCtl.getItemDOMnode().insertBefore($beforeNode);
							} else {
								$itemsEl.append(filterDockItemCtl.getItemDOMnode());
							}
						} else {
							const $element = $(filterDockItemCtl.getItemDOMnode());
							$element.insertBefore(position.insertBefore.node);
						}
						resolve(filterDockItemCtl);
					});
				}
			});

			// Clear empty view.
			this._toggleEmptyView(false);
			this.menu.toggleDisableOverflow(false);

			return addOrUpdateDone;
		},

		_getLeftChevron() {
			if (!this._leftChevron) {
				this._leftChevron = this.$el.find('.filterDockChevronLeft');
			}
			return this._leftChevron;
		},

		_getRightChevron() {
			if (!this._rightChevron) {
				this._rightChevron = this.$el.find('.filterDockChevronRight');
			}
			return this._rightChevron;
		},

		/**
		 * Enable/disable chevrons via swipe gestures.
		 * Still ongoing implemenation so feature is not perfect
		 */
		_enableSwipeChevron(leftChevron, enable) {
			const $chevron = leftChevron ? this._getLeftChevron() : this._getRightChevron();

			$chevron.toggleClass('disable', !enable);
		},

		/**
		 * Enable or disable scrolling (i.e. show chevrons)
		 * Basically show or hide the scrolling chevrons.
		 * @param enable - true to enable scrolling, false to disable
		 */
		_enableScrolling(enable) {
			const $leftChevron = this._getLeftChevron();
			const $rightChevron = this._getRightChevron();

			$leftChevron.toggleClass('invisible', !enable);
			$rightChevron.toggleClass('invisible', !enable);
		},

		/**
		 * Shows a dropdown filter for the provided item.
		 * We assume the item has been scrolled into view in the UI.
		 */
		_showDropdownFilterForItem(item) {
			const filterItemKey = FilterDockItemController.genFilterItemDockKey(item);
			const filterDockItemCtl = this.filterItemsMap[filterItemKey];
			if (filterDockItemCtl) {
				this._scrollItemIntoView(filterDockItemCtl).then(() => {
					filterDockItemCtl.openDropdownFilterView().then(() => {
						filterDockItemCtl.item.setOpenViewOnLoad(false);
						this._setChevronActiveStates();
					});
				});
			}
		},

		/**
		 * Ensures the provided filter dock item is visible.
		 */
		_scrollItemIntoView(filterDockItemCtl) {
			return new Promise((resolve) => {
				const $itemsEl = this._getItemsEl();
				//stop any animations
				$itemsEl.stop();

				const scrollLeft = $itemsEl.scrollLeft();
				const pos = filterDockItemCtl.getItemDOMnode().position();

				$itemsEl.animate({
					scrollLeft: scrollLeft + pos.left
				}, this.chevronAnimationDuration, resolve);
			});
		},

		removeFilterItem(filterDockItem) {
			if (!this.filterItemsMap[filterDockItem.dockKey] || !this.filterItemsMap[filterDockItem.dockKey].remove) {
				this.logger.warn('called from deleteSelf if we do this....!');
			} else {
				this.onAddRemoveFilterItem(filterDockItem.item, this.scope, false);
				this.filterItemsMap[filterDockItem.dockKey].remove();
			}
			delete this.filterItemsMap[filterDockItem.dockKey];
			// Ensure we set the scrolling state and chevrons
			this._enableScrolling(this._shouldWeEnableScrolling());
			this._setChevronActiveStates();

			const count = Object.keys(this.filterItemsMap).length;
			this._toggleEmptyView(count === 0);
			this.menu.toggleDisableOverflow(count === 0);
		},

		remove() {
			if (this._dndTarget) {
				this._dndTarget.remove();
				this._dndTarget = null;
			}
			if (this.moveWidgetSvc) {
				this.moveWidgetSvc.removeDropTarget(this.$el[0]);
				this.moveWidgetSvc = null;
			}
			if (this.htm5DndSvc) {
				this.htm5DndSvc.unregisterDropZone(this);
				this.htm5DndSvc = null;
			}

			this._cleanupFilterItemWrapperInteraction();

			if (this.afi) {
				this.afi.remove();
				this.afi = null;
			}
			$(window).off('resize', this.handlers.windowResizeHandler);
			this.$el.off(TRASITION_ENDED, this.handlers.animationFinished);
			this.off('resize', this._evaluateScrollState, this);
			if (this.filterItemsMap) {
				_.each(this.filterItemsMap, function (object) {
					if (object && object.remove) {
						object.remove();
					}
				});
				this.filterItemsMap = null;
			}

			this.handlers = {};

			FilterDockView.inherited('remove', this, arguments);
		},

		showDock(display) {
			this.$el.toggle(display);
		},

		clickFilterDockIcon() {
			if (this.onClickFilterDockIcon) {
				this.onClickFilterDockIcon(this.scope);
			}
		},

		_toggleEmptyView(toggle) {
			if (!this.$emptyView) {
				this.$emptyView = this.$el.find('.empty-view');
			}
			this.$emptyView.toggleClass('empty', toggle);
		},

		/**
		 * Set the left scrolling chevron's state (enabled or disabled)
		 *
		 * @return {boolean} the active state
		 */
		_setLeftChevronActiveState() {
			let active = false;
			const $itemsEl = this._getItemsEl();
			if ($itemsEl.length > 0) {
				const currentScrollPos = $itemsEl.scrollLeft();
				const $leftChevron = this._getLeftChevron();
				// If we haven't scrolled yet then we can't scroll left, disable.
				$leftChevron.toggleClass('disable', currentScrollPos === 0);
				if (currentScrollPos !== 0) {
					active = true;
				}
			}

			return active;
		},

		/**
		 * Set the right scrolling chevron's state (enabled or disabled)
		 *
		 * @return {boolean} the active state
		 */
		_setRightChevronActiveState() {
			let active = false;
			const $itemsEl = this._getItemsEl();
			if ($itemsEl.length > 0) {
				const currentPosInDiv = Math.round($itemsEl[0].offsetWidth + $itemsEl.scrollLeft() - 0.5); //Round down
				const scrollWidth = $itemsEl[0].scrollWidth;
				const $rightChevron = this._getRightChevron();
				// If we've scrolled to the end we can't scroll to the right anymore, disable
				//disable when reach within 2 pixels
				if (Math.abs(currentPosInDiv - scrollWidth) <= 2) {
					$rightChevron.addClass('disable');
				} else {
					$rightChevron.removeClass('disable');
					active = true;
				}
			}

			return active;
		},

		/**
		 * Set the enabled/disabled state of the scrolling chevrons
		 *
		 * @return {object} the active states for left and chevrons
		 */
		_setChevronActiveStates() {
			return {
				leftActive: this._setLeftChevronActiveState(),
				rightActive: this._setRightChevronActiveState()
			};
		},

		/**
		 * Action handler when a user clicks on the left scrolling chevron.
		 */
		clickOnLeftChevron() {
			const $leftChevron = this._getLeftChevron();
			if ($leftChevron.hasClass('disable')) {
				return;
			}
			const $itemsEl = this._getItemsEl();
			const currentScrollPos = $itemsEl.scrollLeft();
			const scrollAmount = $itemsEl[0].offsetWidth / 2; // Half visible width;

			// Animate a scroll to the new position (curr posiiton - scroll amount)
			$itemsEl.animate({
				scrollLeft: currentScrollPos - scrollAmount
			}, this.chevronAnimationDuration, this._setChevronActiveStates.bind(this));
		},

		/**
		 * Action handler when a user clicks on the right scrolling chevron.
		 */
		clickOnRightChevron() {
			const $rightChevron = this._getRightChevron();
			if ($rightChevron.hasClass('disable')) {
				return;
			}
			const $itemsEl = this._getItemsEl();
			const currentScrollPos = $itemsEl.scrollLeft();
			const scrollAmount = $itemsEl[0].offsetWidth / 2; // Half visible width;

			// Animate a scroll to the new position (curr posiiton + scroll amount)
			$itemsEl.animate({
				scrollLeft: currentScrollPos + scrollAmount
			}, this.chevronAnimationDuration, this._setChevronActiveStates.bind(this));
		},

		isExpanded() {
			return this.$el.hasClass('filterDockExpanded');
		},

		isCollapsed() {
			return this.$el.hasClass('filterDockCollapsed');
		},

		isSizedByDrag() {
			return this.$el.hasClass('d-dragged');
		},

		toggleCollapse() {
			if (this.$el.toggleClass('filterDockCollapsed').hasClass('filterDockCollapsed')) {
				this.$el.removeClass('filterDockExpanded');
				this.afi.show(true);
				return true;
			}
		},

		expandDock() {
			this.$el.addClass('filterDockExpanded');
			this.$el.removeClass('filterDockCollapsed');
			this.afi.show(false);
			this._getItemsEl().fadeIn();
		},

		resetDockWidth() {
			this._enableScrolling(false);
			this.$el.removeClass('filterDockCollapsed filterDockExpanded d-dragged');
			this.$el.attr('style', null); // Warning: This blows out all the style properties
			this.afi.show(false);
			this._getItemsEl().fadeIn();
		},

		/**
		 * @returns the $el of to mount the action drop down menu.
		 */
		getFilterMenuMountNode() {
			return this.$('.filter-menu');
		},

		/**
		 * Returns the filter item identify by dockKey
		 *
		 * @param {string} dockKey - the dock key use to return the filter item
		 *
		 * @return {object} The retrieved filter item if one found else return null
		 */
		getFilterItem(dockKey) {
			return (dockKey && this.filterItemsMap[dockKey]) ? this.filterItemsMap[dockKey].item : null;
		},

		/**
		 * Cleans up all fiter item wrapper items interactions such as drag and drop and swipe
		 */
		_cleanupFilterItemWrapperInteraction() {
			if (this.itemsContainer) {
				this.itemsContainer.destroy();
				this.itemsContainer = null;
			}

			if (this.swipeGesture) {
				this.swipeGesture.destroy();
				this.swipeGesture = null;
			}
		},

		/**
		 * Set up  filterItemWrapper interaction support such as drag and drop and swipe
		 */
		_setupFilterItemWrapperInteraction() {
			this._cleanupFilterItemWrapperInteraction();
			this.itemsContainer = new FilterItemsContainer({
				$el: this._getItemsEl(),
				id: this.id + '_' + this.viewId,
				dashboardApi: this.dashboardApi,
				logger: this.logger,
				pageContextAPI: this.pageContextAPI,
				handlers: {
					getFilterItem: this.getFilterItem.bind(this),
					chevron: {
						onClickOnLeftChevron: this.clickOnLeftChevron.bind(this),
						onClickOnRightChevron: this.clickOnRightChevron.bind(this),
						getLeftChevron: this._getLeftChevron.bind(this),
						getRightChevron: this._getRightChevron.bind(this),
						delay: this.chevronAnimationDuration
					}
				}
			});

		},

		toggleDropdownMenu(event) {
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			this.menu.toggle();
		},

		closeDropdownMenu() {
			if (this.menu.isOpen()) {
				this.toggleDropdownMenu();
			}
			this.$('.filter-dock-overflow').focus();
		},

		hideMenu() {
			this.menu.hide();
		},

		showMenu() {
			this.menu.show();
		},

		_getDnD_uploadedTableRef(dragObject, metadataColumn) {
			if (dragObject && dragObject.data && dragObject.data.sourceId) {
				const dataSources = this.dashboardApi.getFeature('DataSources');
				return DatasourceUtil.getTableRef(dataSources.getDataSource(dragObject.data.sourceId), [ metadataColumn.getId()]);
			}
		}
	});

	return FilterDockView;
});
